import { ReactNode, useState } from "react";
import { SessionResultsDto, OptInStatus, BallotCandidate } from "../types";
import EventDetailsModal from "./EventDetailsModal";
import AddToCalendar from "./AddToCalendar";

const Chip: React.FC<{ children: ReactNode; tone?: "good" | "muted" }> = ({ children, tone = "muted" }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
      tone === "good" ? "bg-green-100 text-green-700" : "bg-cream-200 text-ink/60"
    }`}
  >
    {children}
  </span>
);

const optInLabel = (s: OptInStatus) => (s === "OptedIn" ? "in" : s === "Declined" ? "out" : "invited");

/**
 * Shared results render used by the organizer and public pages. When `onVote` is
 * provided (organizer/member during voting), each option becomes a tappable vote
 * button highlighting the viewer's current pick.
 */
const ResultsBody: React.FC<{
  results: SessionResultsDto;
  onVote?: (suggestionId: string) => void;
  onRemove?: (suggestionId: string) => void;
  voting?: boolean;
}> = ({ results, onVote, onRemove, voting = false }) => {
  const maxVotes = Math.max(1, ...results.candidates.map((c) => c.voteCount));
  const canVote = voting && !!onVote;
  const [detail, setDetail] = useState<BallotCandidate | null>(null);

  return (
    <div className="space-y-6">
      {results.decision && (
        <div className="rounded-3xl bg-gradient-to-br from-primary-500 to-primary-700 p-6 text-white shadow-glow">
          <div className="text-xs uppercase tracking-widest opacity-80">🎬 The plan</div>
          <div className="mt-1.5 font-display text-2xl font-semibold leading-tight">
            {results.decision.winningTitle}
          </div>
          {results.decision.rationale && (
            <p className="mt-2 text-sm italic text-white/80">"{results.decision.rationale}"</p>
          )}
          <AddToCalendar
            title={results.decision.winningTitle}
            startsAt={results.decision.startsAt}
            location={results.candidates.find((c) => c.id === results.decision!.winningSuggestionId)?.title}
            url={results.candidates.find((c) => c.id === results.decision!.winningSuggestionId)?.url}
          />
        </div>
      )}

      <section>
        <h2 className="font-display text-lg font-semibold text-ink">Who's in</h2>
        <ul className="mt-2 space-y-1.5">
          {results.participants.length === 0 && <li className="text-sm text-ink/40">No one invited yet.</li>}
          {results.participants.map((p) => (
            <li key={p.memberId} className="rounded-xl bg-white/60 px-3 py-2 text-sm ring-1 ring-primary-100">
              <div className="flex items-center justify-between">
                <span className="font-medium text-ink">{p.displayName}</span>
                <span className="flex items-center gap-1.5">
                  <Chip>{optInLabel(p.optInStatus)}</Chip>
                  {p.votedSuggestionIds.length > 0 ? (
                    <Chip tone="good">voted</Chip>
                  ) : p.included ? (
                    <Chip>thinking…</Chip>
                  ) : null}
                </span>
              </div>
              {p.liked.length > 0 && (
                <p className="mt-1 text-xs text-ink/55">
                  <span className="text-primary-500">♥</span> {p.liked.join(", ")}
                </p>
              )}
            </li>
          ))}
        </ul>
      </section>

      {results.candidates.length > 0 && (
        <section>
          <h2 className="font-display text-lg font-semibold text-ink">
            {canVote ? "Tap to vote (up to 2)" : "Options"}
          </h2>
          <div className="mt-2 space-y-2.5">
            {results.candidates.map((c) => {
              const voters = results.participants
                .filter((p) => p.votedSuggestionIds.includes(c.id))
                .map((p) => p.displayName);
              const isWinner = results.decision?.winningSuggestionId === c.id;
              const mine = results.yourVoteSuggestionIds.includes(c.id);
              const tone = mine
                ? "border-primary-500 bg-primary-50 ring-1 ring-primary-300"
                : isWinner
                ? "border-primary-400 bg-primary-50"
                : "border-primary-100 bg-white/60";
              const inner = (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display font-semibold text-ink">{c.title}</span>
                    <span className="text-sm text-ink/55">
                      {c.voteCount} vote{c.voteCount === 1 ? "" : "s"}
                    </span>
                  </div>
                  {c.description && <p className="mt-1 text-sm text-ink/65">{c.description}</p>}
                  {c.warning && <p className="mt-1 text-xs font-medium text-amber-700">⚠ {c.warning}</p>}
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-cream-200">
                    <div
                      className="h-full rounded-full bg-primary-500 transition-all duration-500"
                      style={{ width: `${(c.voteCount / maxVotes) * 100}%` }}
                    />
                  </div>
                  {voters.length > 0 && <p className="mt-1.5 text-xs text-ink/50">{voters.join(", ")}</p>}
                  {mine && <p className="mt-1 text-xs font-medium text-primary-700">Your pick</p>}
                </>
              );
              const hasDetails = !!c.url || (c.latitude != null && c.longitude != null);
              const detailsButton = hasDetails ? (
                <button
                  onClick={() => setDetail(c)}
                  className="ml-1 mt-1 inline-block text-xs font-medium text-dusk-500 hover:text-dusk-700"
                >
                  View details
                </button>
              ) : null;
              return (
                <div key={c.id}>
                  {canVote ? (
                    <button
                      onClick={() => onVote!(c.id)}
                      className={`w-full rounded-2xl border p-3.5 text-left transition-colors hover:border-primary-300 ${tone}`}
                    >
                      {inner}
                    </button>
                  ) : (
                    <div className={`rounded-2xl border p-3.5 ${tone}`}>{inner}</div>
                  )}
                  {detailsButton}
                  {onRemove && (
                    <button
                      onClick={() => {
                        if (window.confirm(`Remove "${c.title}" from the vote?`)) onRemove(c.id);
                      }}
                      className="ml-3 mt-1 inline-block text-xs font-medium text-ink/40 hover:text-red-600"
                    >
                      Remove
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <EventDetailsModal candidate={detail} onClose={() => setDetail(null)} />
    </div>
  );
};

export default ResultsBody;
